/**
 * #1914 — why a worker cannot be assigned, in one place, and said out loud.
 *
 * ══ THE DEFECT IS SILENCE, NOT A WRONG OFFER ══
 *
 * Both screens already blocked a vacationing worker: `assignHardBlocked()` was
 * BYTE-IDENTICAL in `bulk.vue` and `single.vue` and both tested the resolver for
 * vacation / recurring. What differed is what the row then showed:
 *
 *   single.vue:210   keeps the control and adds an `isDisabled` class
 *   bulk.vue:220     REMOVES the checkbox with `v-if` — no control, no marker
 *
 * So on bulk the row went quiet and a dispatcher was left to guess why somebody
 * could not be picked. ed's report — "would not assign while still showing as
 * available" — is what that looks like from the outside.
 *
 * ⚠ THIS IS AN EXTRACTION, NOT A BEHAVIOUR CHANGE. Who is blocked does not move.
 * Two identical copies of the rule in two components is how they would have
 * drifted, and it is the #1873 lesson: one definition, both consumers.
 */

/** Resolver reasons that make a worker genuinely unassignable. Mirrors AvailabilityCheck::HARD_BLOCK_REASONS. */
const HARD_REASONS = ['vacation', 'recurring'];

/** Per-call action states that block for their own window. */
const BLOCKING_ACTIONS = {
    'Call Out': 'Called out',
    'Callout': 'Called out',
    'Declined': 'Declined',
    'No-show': 'No-show',
    'Sent Home': 'Sent home',
};

function reasonsIn(conflict) {
    if (!conflict) {
        return [];
    }
    const primary = conflict.primary && conflict.primary.reason;

    return [primary].concat((conflict.others || []).map((o) => o && o.reason)).filter(Boolean);
}

function blockingAction(callActions) {
    const found = (callActions || []).find((a) => a && BLOCKING_ACTIONS[a.action_name]);

    return found ? BLOCKING_ACTIONS[found.action_name] : '';
}

/**
 * Does this worker's own availability pattern block the call?
 *
 * Kept as a hook so the components pass their existing helper in rather than
 * this module re-deriving a weekly pattern it has no business parsing.
 */
function patternBlocks(employee, recurringUnavailable) {
    return typeof recurringUnavailable === 'function'
        ? !!recurringUnavailable(employee.available_times)
        : false;
}

/**
 * ⚠ THE DECISION, UNCHANGED. If this moves, the fix has drifted into altering
 * who may be assigned — which is not what #1914 is.
 */
export function isHardBlocked(employee, recurringUnavailable) {
    if (!employee) {
        return false;
    }
    const hard = reasonsIn(employee.conflict).some((r) => HARD_REASONS.indexOf(r) !== -1);

    return hard || patternBlocks(employee, recurringUnavailable) || blockingAction(employee.call_actions) !== '';
}

/**
 * What to show on the row.
 *
 * ⚠ ALREADY IN THE RESPONSE. `conflict.primary.label` has shipped since #1661 —
 * nothing new is fetched to name the reason, and nothing beyond the label is
 * shown. #1821 scoped the client name to the PILL and this marker must not
 * widen that: no job id, no call id, no other tenant's window.
 */
export function blockReason(employee, recurringUnavailable) {
    if (!isHardBlocked(employee, recurringUnavailable)) {
        return '';
    }

    const conflict = employee.conflict;
    const hard = reasonsIn(conflict).find((r) => HARD_REASONS.indexOf(r) !== -1);
    if (hard) {
        const primary = conflict.primary || {};
        if (primary.reason === hard && primary.label) {
            return primary.label;
        }
        const other = (conflict.others || []).find((o) => o && o.reason === hard);
        if (other && other.label) {
            return other.label;
        }

        return hard === 'vacation' ? 'Vacation' : 'Recurring unavailable';
    }

    const action = blockingAction(employee.call_actions);
    if (action) {
        return action;
    }

    // A raw weekly pattern with nothing in `conflict` — the last silent case.
    return 'Unavailable';
}

/**
 * ⚠ VISIBILITY, NOT PERMISSION. The control appears for a blocked worker so the
 * row can explain itself; it is never selectable.
 */
export function isSelectable(employee, recurringUnavailable) {
    return !isHardBlocked(employee, recurringUnavailable);
}
